import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import IntakeCard from '../screens/Onboarding/IntakeCard.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { api } from '../services/api/client.js';
import { clearOnboardingSkipped } from '../lib/onboardingSkip.js';
import { markOnboardingCompleted } from '../lib/onboardingCompletion.js';
import { track } from '../lib/funnel.js';
import {
  accountBrandName,
  BRAND_CREATED_EVENT,
  createMinimalBrand,
  createReviewedQuickBrand,
  isMeaningfulBusinessDescription,
  scanQuickBrandWebsite,
} from '../lib/quickBrand.js';
import { rasterizeSvgToPng } from '../lib/rasterize.js';

const LANGUAGES = [
  ['en', 'English'], ['uk', 'Українська'], ['ru', 'Русский'], ['de', 'Deutsch'],
  ['es', 'Español'], ['pl', 'Polski'], ['cs', 'Čeština'], ['lt', 'Lietuvių'],
  ['lv', 'Latviešu'], ['sv', 'Svenska'],
];

export default function QuickBrandSheet({ open, source, onClose, onCreated }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth() || {};
  const dashboardData = useDashboardData();
  const defaultName = accountBrandName(user);
  const [name, setName] = useState(defaultName);
  const [website, setWebsite] = useState('');
  const [language, setLanguage] = useState(() => String(i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('basics');
  const [scan, setScan] = useState(null);
  const [manualBrief, setManualBrief] = useState('');
  const [reviewedDraft, setReviewedDraft] = useState(null);
  const nameRef = useRef(null);
  const scanIdRef = useRef(0);
  const busyRef = useRef(false);
  const savingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  busyRef.current = busy;
  savingRef.current = busy && step !== 'scanning';
  onCloseRef.current = () => {
    if (savingRef.current) return;
    scanIdRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return undefined;
    setName(defaultName);
    setWebsite('');
    setLanguage(String(i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]);
    setError('');
    setBusy(false);
    setStep('basics');
    setScan(null);
    setManualBrief('');
    setReviewedDraft(null);
    const raf = requestAnimationFrame(() => nameRef.current?.focus());
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      scanIdRef.current += 1;
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, defaultName, i18n.language, i18n.resolvedLanguage]);

  if (!open) return null;

  const finishCreated = async (result) => {
    api.invalidateCache?.(['/api/brand-profile', '/api/profile/brands', '/api/onboarding/status', '/api/strategy']);
    clearOnboardingSkipped(user?.id);
    markOnboardingCompleted();
    await Promise.allSettled([
      dashboardData.invalidateResource?.('setupStatus'),
      dashboardData.invalidateResource?.('socialAccounts'),
    ]);
    window.dispatchEvent(new CustomEvent(BRAND_CREATED_EVENT, { detail: { name: name.trim() } }));
    try { track('quick_brand_created', { source }); } catch { /* fail-silent */ }
    onCreated?.(result);
  };

  const createPersonal = async (event) => {
    event?.preventDefault?.();
    if (busyRef.current) return;
    if (!name.trim()) {
      setError(t('quickBrand.nameRequired'));
      nameRef.current?.focus();
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await createMinimalBrand({
        apiClient: api,
        businessName: name,
        website: '',
        language,
      });
      await finishCreated(result);
    } catch (err) {
      if (err?.message === 'INVALID_WEBSITE') setError(t('quickBrand.websiteInvalid'));
      else setError(err?.message || t('quickBrand.createFailed'));
      busyRef.current = false;
      setBusy(false);
    }
  };

  const rasterizeLogo = async (dataUrl) => {
    const blob = await (await fetch(dataUrl)).blob();
    const png = await rasterizeSvgToPng(blob);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(png);
    });
  };

  const runScan = async () => {
    if (busyRef.current || !name.trim()) return;
    if (!website.trim()) { await createPersonal(); return; }
    const id = ++scanIdRef.current;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setStep('scanning');
    try {
      const result = await scanQuickBrandWebsite({ apiClient: api, businessName: name, website, rasterizeLogo });
      if (scanIdRef.current !== id) return;
      setScan(result);
      if (!isMeaningfulBusinessDescription(result.business?.businessDescription)) setError(t('quickBrand.briefRequired'));
      else if (result.logoUnavailable) setError(t('quickBrand.logoScanFailed'));
      setStep('review');
    } catch (err) {
      if (scanIdRef.current !== id) return;
      setScan(null);
      setStep('failed');
      setError(err?.message === 'INVALID_WEBSITE' ? t('quickBrand.websiteInvalid') : t('quickBrand.scanFailed'));
    } finally {
      if (scanIdRef.current === id) { busyRef.current = false; setBusy(false); }
    }
  };

  const accept = async (reviewed) => {
    if (busyRef.current) return;
    const fields = { ...(reviewed?.fields || {}) };
    if (!isMeaningfulBusinessDescription(fields.businessDescription) && isMeaningfulBusinessDescription(manualBrief)) {
      fields.businessDescription = manualBrief.trim();
    }
    if (!isMeaningfulBusinessDescription(fields.businessDescription)) {
      setError(t('quickBrand.briefRequired'));
      setManualBrief(fields.businessDescription || '');
      setReviewedDraft(reviewed);
      setStep('manual');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await createReviewedQuickBrand({
        apiClient: api, businessName: name, website, language,
        reviewed: { ...reviewed, fields }, confidence: scan?.confidence,
        websiteAnalysis: scan?.business?.websiteAnalysis,
        manualDescription: step === 'manual',
      });
      await finishCreated(result);
    } catch (err) {
      setError(err?.code === 'LOGO_SAVE_FAILED' ? t('quickBrand.logoSaveFailed') : (err?.message || t('quickBrand.createFailed')));
      busyRef.current = false;
      setBusy(false);
    }
  };

  const invalidateScan = (setter) => (event) => {
    scanIdRef.current += 1;
    setScan(null);
    setReviewedDraft(null);
    setStep('basics');
    setError('');
    setter(event.target.value);
  };

  return (
    <div className="upm-scrim qb-scrim" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onCloseRef.current?.();
    }}>
      <div className={`upm-card qb-card${step === 'review' || step === 'scanning' ? ' qb-card--review' : ''}`} role="dialog" aria-modal="true" aria-labelledby="qb-title">
        <button type="button" className="upm-close" onClick={() => onCloseRef.current?.()} disabled={busy && step !== 'scanning'} aria-label={t('quickBrand.close')}>
          <Icon name="x" size={18} />
        </button>
        <div className="qb-head">
          <span className="qb-mark" aria-hidden="true"><Icon name="sparkles" size={20} /></span>
          <div>
            <h2 id="qb-title" className="upm-title">{t('quickBrand.title')}</h2>
            <p className="upm-sub">{t('quickBrand.subtitle')}</p>
          </div>
        </div>
        {step === 'basics' && <form className="qb-fields" onSubmit={(event) => { event.preventDefault(); runScan(); }}>
          <label className="ppw-field">
            <span>{t('quickBrand.nameLabel')}</span>
            <span className="ppw-inp"><input ref={nameRef} value={name} onChange={invalidateScan(setName)} disabled={busy} /></span>
          </label>
          <label className="ppw-field">
            <span>{t('quickBrand.websiteLabel')} <span className="ppw-opt">{t('quickBrand.optional')}</span></span>
            <span className="ppw-inp"><input type="text" inputMode="url" value={website} onChange={invalidateScan(setWebsite)} placeholder={t('quickBrand.websitePlaceholder')} disabled={busy} /></span>
          </label>
          <label className="ppw-field">
            <span>{t('quickBrand.languageLabel')}</span>
            <span className="ppw-inp"><select value={language} onChange={(event) => setLanguage(event.target.value)} disabled={busy}>
              {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select></span>
          </label>
          <button type="submit" hidden aria-hidden="true" />
        </form>}
        {error && <div className="ppw-err" role="alert">{error}</div>}
        {step === 'scanning' && <div className="qb-intake"><IntakeCard status="detecting" data={{ domain: website }} busy /></div>}
        {step === 'review' && scan && <div className="qb-intake"><IntakeCard key={`${name}|${website}`} variant="quick-brand" status="found" data={{ domain: scan.domain, business: scan.business, logo: scan.logo, colors: scan.colors, styleTags: scan.styleTags }} busy={busy} onConfirm={accept} onSkip={() => { setScan(null); setReviewedDraft(null); setStep('basics'); setError(''); }} onTellAbout={() => setStep('manual')} /></div>}
        {step === 'failed' && <div className="qb-fallback"><p>{t('quickBrand.scanFallback')}</p></div>}
        {step === 'manual' && <label className="ppw-field qb-manual">
          <span>{t('quickBrand.briefLabel')}</span>
          <span className="ppw-inp"><textarea value={manualBrief} onChange={(event) => setManualBrief(event.target.value)} rows={3} placeholder={t('quickBrand.briefPlaceholder')} disabled={busy} /></span>
        </label>}
        {step !== 'review' && <div className="ppw-acts qb-actions">
          <button type="button" className="ppw-btn ppw-btn-ghost" onClick={() => onCloseRef.current?.()} disabled={busy && step !== 'scanning'}>{t('quickBrand.cancel')}</button>
          {step === 'basics' && <button type="button" className="ppw-btn ppw-btn-primary" onClick={runScan} disabled={busy || !name.trim()}>{website.trim() ? t('quickBrand.scan') : t('quickBrand.quickSetup')}</button>}
          {step === 'failed' && <><button type="button" className="ppw-btn ppw-btn-ghost" onClick={() => setStep('manual')}>{t('quickBrand.enterBrief')}</button><button type="button" className="ppw-btn ppw-btn-primary" onClick={runScan}>{t('quickBrand.retryScan')}</button></>}
          {step === 'manual' && reviewedDraft?.logo && <button type="button" className="ppw-btn ppw-btn-ghost" disabled={busy} onClick={() => setReviewedDraft((draft) => ({ ...draft, logo: null }))}>{t('onboardingChat.intake.skip')} {t('onboardingChat.intake.logo')}</button>}
          {step === 'manual' && <button type="button" className="ppw-btn ppw-btn-primary" onClick={() => accept({ ...reviewedDraft, fields: { ...(reviewedDraft?.fields || {}), businessDescription: manualBrief } })} disabled={busy || !isMeaningfulBusinessDescription(manualBrief)}>{busy ? t('quickBrand.creating') : t('quickBrand.create')}</button>}
        </div>}
      </div>
    </div>
  );
}
